import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import FinanceShell from "@/components/finance/FinanceShell";
import { formatTRY } from "@/components/finance/financeLabels";
import { TrendingUp, TrendingDown, Wallet, AlertCircle, RefreshCw, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };

export default function FinanceDashboardPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [from, setFrom] = useState(startOfMonth());
    const [to, setTo] = useState(today());

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/finance/dashboard?date_from=${from}&date_to=${to}`);
            setData(r.data);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

    return (
        <FinanceShell>
            <div className="flex flex-wrap items-end gap-3 mb-5">
                <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Başlangıç</label>
                    <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="block px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm" data-testid="fin-dash-from" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Bitiş</label>
                    <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="block px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm" data-testid="fin-dash-to" />
                </div>
                <button onClick={load} className="px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm inline-flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Yenile</button>
            </div>

            {loading || !data ? (
                <div className="py-12 text-center text-slate-500">Yükleniyor...</div>
            ) : (
                <>
                    {/* KPI Cards */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        <KpiCard label="Tahsilat" value={data.income.booking_payments} count={data.income.count} icon={TrendingUp} tone="emerald" testid="kpi-income" />
                        <KpiCard label="İade" value={data.refunds.total} count={data.refunds.count} icon={TrendingDown} tone="rose" testid="kpi-refunds" />
                        <KpiCard label="Net Sonuç" value={data.net} icon={TrendingUp} tone={data.net >= 0 ? "sky" : "rose"} testid="kpi-net" />
                        <KpiCard label="Toplam Kasa" value={data.total_cash} icon={Wallet} tone="violet" testid="kpi-cash" />
                    </div>

                    {/* Alerts */}
                    {(data.refunds.pending_requests > 0 || data.refunds.approved_pending_process > 0) && (
                        <Link to="/admin/finance/refunds" className="block mb-6 p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition" data-testid="fin-refund-alert">
                            <div className="flex items-center gap-3">
                                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                                <div className="flex-1">
                                    <div className="font-bold text-amber-900 dark:text-amber-100">İade işlemleri bekliyor</div>
                                    <div className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                                        {data.refunds.pending_requests} onay bekleyen · {data.refunds.approved_pending_process} işleme alınmamış
                                    </div>
                                </div>
                                <ArrowRight className="h-4 w-4 text-amber-600" />
                            </div>
                        </Link>
                    )}

                    {/* Cash balances */}
                    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
                            <h3 className="font-bold">Kasa & Banka Bakiyeleri</h3>
                            <Link to="/admin/finance/cash" className="text-xs text-sky-600 hover:underline">Tümünü Gör →</Link>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
                            {(data.cash_balances || []).map((c) => (
                                <div key={c.id} className="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-4" data-testid={`cash-${c.code}`}>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{c.code}</div>
                                    <div className="font-semibold text-slate-800 dark:text-slate-200 mt-0.5 text-sm">{c.name}</div>
                                    <div className="text-2xl font-bold mt-2">{formatTRY(c.balance)}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Expenses breakdown */}
                    <div className="grid sm:grid-cols-3 gap-3 mt-6">
                        <ExpenseRow label="Acente Komisyon" value={data.expenses.commission} />
                        <ExpenseRow label="Tedarikçi Ödemeleri" value={data.expenses.supplier_payment} />
                        <ExpenseRow label="Genel Giderler" value={data.expenses.expense} />
                    </div>
                </>
            )}
        </FinanceShell>
    );
}

function KpiCard({ label, value, count, icon: Icon, tone, testid }) {
    const map = {
        emerald: "from-emerald-500 to-emerald-600 text-emerald-100",
        rose: "from-rose-500 to-rose-600 text-rose-100",
        sky: "from-sky-500 to-sky-600 text-sky-100",
        violet: "from-violet-500 to-violet-600 text-violet-100",
    };
    return (
        <div className={`rounded-2xl p-5 bg-gradient-to-br ${map[tone] || map.sky} text-white shadow-md`} data-testid={testid}>
            <div className="flex items-start justify-between">
                <div className="text-xs font-bold uppercase tracking-wider opacity-90">{label}</div>
                <Icon className="h-5 w-5 opacity-80" />
            </div>
            <div className="text-3xl font-extrabold mt-2">{formatTRY(value)}</div>
            {typeof count === "number" && <div className="text-xs opacity-80 mt-1">{count} işlem</div>}
        </div>
    );
}

function ExpenseRow({ label, value }) {
    return (
        <div className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
            <div className="text-xs text-slate-500 font-semibold">{label}</div>
            <div className="text-xl font-bold mt-1 text-rose-600 dark:text-rose-400">{formatTRY(value)}</div>
        </div>
    );
}
